import React, { useRef, useState, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { products } from "../../utils/productsData";
import SEO from "../../components/SEO";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ScrollToTopButton from "../../components/ScrollTop";
import WhatsAppMsg from "../../components/WhatsApp";
import FAQ from "../../components/FAQ";
import { faqData } from "../../utils/landingFAQ";
import TestimonialCarousel from "../../components/TestimonialCarousel";
import { UserContext } from "../../utils/UserContext";
import { saveProductView } from "../../utils/saveProductView";
import { Link } from "react-router-dom";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
// import required modules
import { Pagination } from "swiper/modules";
import { slides } from "../../utils/featureSlides";
import { features } from "../../utils/featureDetails";
import { testimonials } from "../../utils/testimonials";
import { clientImages } from "../../utils/clientImages";
import { Helmet } from "react-helmet";
import InertiaFeatures from "./active-led/InertiaFeatures";
import VistaFeatures from "./commercial-display/VistaFeatures";
import CustomerReviews from "../../components/CustomerReviews";

// Get base URL from .env file
const baseUrl = process.env.REACT_APP_API_URL;

const ProductDetails = () => {
  const { slug } = useParams();
  // Consume UserContext
  const { user, openSignInModal } = useContext(UserContext);
  const navigate = useNavigate();
  // Refs for the central images
  const imagesRef = useRef([]);
  // Local state for quantity
  const [quantity, setQuantity] = useState(1);
  const [toast, setToast] = useState({
    showToast: false,
    message: "",
    type: "",
  });

  // This state will track if product is already added to cart
  const [addedToCart, setAddedToCart] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const SLIDE_DURATION = 10000; // in ms
  const STEPS_PER_SLIDE = 3;

  // Current slide (0 or 1 here, but you can have more)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  // Progress (0..1) within the current 30s slide cycle
  const [progress, setProgress] = useState(0);

  // Track the start time of the current slide in a ref
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    let animationFrameId;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      let fraction = elapsed / SLIDE_DURATION; // 0..1

      if (fraction >= 1) {
        // Move to next slide
        const nextSlide = (currentSlideIndex + 1) % slides.length;
        setCurrentSlideIndex(nextSlide);

        // Reset for new slide
        startTimeRef.current = now;
        fraction = 0;
      }

      setProgress(Math.min(fraction, 1));
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [currentSlideIndex]);

  // Which step is currently active (0..2)
  const stepIndex = Math.floor(progress * STEPS_PER_SLIDE);

  // Get the active slide data
  const { steps } = slides[currentSlideIndex];
  // For the left side image, we show only the active step's image
  const activeStep = steps[stepIndex];

  // Helper: calculate the blue fill height (in pixels) for the connecting line below a given step circle
  const computeFillHeightForLine = (i) => {
    const containerHeight = 50; // Same as h-[50px]
    if (stepIndex > i) {
      // This step is complete – the line is fully filled.
      return containerHeight;
    } else if (stepIndex === i) {
      // Currently transitioning – fill proportionally.
      const fractionForCurrentStep = progress * STEPS_PER_SLIDE - stepIndex;
      return fractionForCurrentStep * containerHeight;
    } else {
      // Not yet reached – no fill.
      return 0;
    }
  };

  // Flatten all products into an array
  const allProducts = Object.values(products).flat();

  // Find the product by the current URL param `slug`
  const product = allProducts.find((p) => p.slug === slug);
  // Get the current URL path from the router location
  const pagePath = window.location.href;
  useEffect(() => {
    // If you want to call it only when user is logged in, wrap in an if(user) block
    // But if you always want to track visits, you can do user?.email || "" for email
    if (product) {
      saveProductView({
        email: user?.email || "",
        value: pagePath,
        // or you can do value: "65-inch-coltec-rise-powerful-interactive-flat-panel" if it’s specifically that
      }).catch((err) => {
        console.error("Error calling saveProductView:", err);
      });
    }
  }, [product, user, pagePath]); // triggers whenever product or user changes

  // If no product found, show 404-like message
  if (!product) {
    return <div>Product not found!</div>;
  }

  // Set flags for each product type.
  const isInteractive = product.name
    .toLowerCase()
    .includes("interactive flat panel");
  const isActiveLED = product.name
    .toLowerCase()
    .includes("active led video wall");
  const isCommercial = product.name
    .toLowerCase()
    .includes("commercial display");

  // Extract the brand part from the product name (e.g., "COLTEC Rise")
  const brandPart = product.name.split("|")[0].trim();
  const sameBrandProducts = allProducts.filter((p) =>
    p.name.includes(brandPart)
  );

  // Helper to extract the numeric size from a string (e.g., "65" from "65 Inch Smart Interactive Flat Panel")
  const extractSizeNumber = (sizeString) => {
    const match = sizeString.match(/\d+/);
    return match ? match[0] : "";
  };

  // Dynamically generate allowed sizes from same-brand products data
  const sizesFromProducts = sameBrandProducts.map((p) => {
    const sizePart = p.name.split("|")[1]?.trim() || "";
    return extractSizeNumber(sizePart);
  });

  // Get unique allowed sizes and sort them numerically
  const allowedSizes = Array.from(new Set(sizesFromProducts)).sort(
    (a, b) => parseInt(a) - parseInt(b)
  );

  // Get the current product’s size (numeric only)
  const sizePartCurrent = product.name.split("|")[1]?.trim() || "";
  let currentSize = extractSizeNumber(sizePartCurrent);
  // If the current product’s size is not in allowedSizes, default to the first allowed size
  if (!allowedSizes.includes(currentSize)) {
    currentSize = allowedSizes[0];
  }

  // Filter the same-brand products to only include products with allowed sizes
  const filteredSameBrandProducts = sameBrandProducts.filter((p) => {
    const sizePart = p.name.split("|")[1]?.trim() || "";
    const sizeNumber = extractSizeNumber(sizePart);
    return allowedSizes.includes(sizeNumber);
  });

  // Use the dynamically generated allowed sizes for the dropdown options
  const dropdownSizes = allowedSizes;

  // When the user selects a new size from the dropdown, navigate to the matching product page.
  const handleSizeChange = (e) => {
    const newSize = e.target.value; // will be "65" or "86"
    const newProduct = filteredSameBrandProducts.find((p) => {
      const sizePart = p.name.split("|")[1]?.trim() || "";
      return extractSizeNumber(sizePart) === newSize;
    });
    if (newProduct) {
      navigate(`/product-details/${newProduct.slug}`);
    }
  };

  // Scroll into the corresponding full image when thumbnail is clicked
  const handleThumbnailClick = (index) => {
    if (imagesRef.current[index]) {
      imagesRef.current[index].scrollIntoView({ behavior: "smooth" });
    }
  };

  // Increment/Decrement quantity, min 1
  const decreaseQuantity = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };
  const increaseQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  // 1. Handle "Add to Cart"
  const handleAddToCart = async () => {
    if (!user) {
      // User not logged in
      openSignInModal();
      return;
    }
    // Convert the price from string to integer
    const priceAsInt = parseInt(product.price.replace(/[^0-9]/g, ""), 10);
    // Build the form data
    const formData = {
      name: product.name,
      imageUrl:
        product.gallery && product.gallery.length > 0 ? product.gallery[0] : "",
      quantity,
      panelSize: currentSize,
      price: priceAsInt,
      email: user.email, // user from context
    };

    try {
      const response = await fetch(`${baseUrl}/api/Coltec/AddCartProduct`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to add to cart");
      }

      // Switch button to "Continue Shopping" mode
      setAddedToCart(true);

      // Show success toast
      setToast({
        showToast: true,
        message: "Product added to cart!",
        type: "success",
      });
      setTimeout(() => {
        setToast({ showToast: false, message: "", type: "" });
      }, 2000);
    } catch (error) {
      console.error("Error adding to cart:", error);
      // Show an error toast
      setToast({
        showToast: true,
        message: "Failed to add product to cart. Please try again.",
        type: "error",
      });
      setTimeout(() => {
        setToast({ showToast: false, message: "", type: "" });
      }, 2000);
    }
  };

  // 2. Handle "View Cart" -> GET cart data -> navigate to /cart
  const handleViewCart = async () => {
    if (!user) {
      openSignInModal();
      return;
    }

    // Dynamically get the user's email from the user context
    const userEmail = user.email;

    try {
      const response = await fetch(
        `${baseUrl}/api/Coltec/GetCartDetailsByEmail?Email=${encodeURIComponent(
          userEmail
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Add any necessary authentication or headers if needed
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch cart data");
      }
      // Do something with cartData (e.g., update global state or navigate to the cart page)

      // Navigate to /cart
      navigate("/products/cart");
    } catch (error) {
      console.error("Error fetching cart data:", error);
      setToast({
        showToast: true,
        message: "Failed to fetch cart data.",
        type: "error",
      });
      setTimeout(() => {
        setToast({ showToast: false, message: "", type: "" });
      }, 2000);
    }
  };

  return (
    <>
      {/* SEO */}
      <div className="blogs-page">
        <SEO
          title={`${product.title} at ${product.price}`}
          description={product.metaDescription}
          keywords={product.keywords}
          ogTitle={product.name}
          ogDescription={product.metaDescription}
          ogImage={product.imageUrl}
          ogUrl={`https://coltec-global.com/product-details/${product.slug}`}
          canonicalUrl={`https://coltec-global.com/product-details/${product.slug}`}
          ogSiteName="Coltec Global"
          ogType="website"
          twitterSite="https://x.com/ColtecGlobal" // Twitter username associated with the site
          twitterTitle={`${product.title} at ${product.price}`}
          twitterDescription={product.metaDescription}
          twitterImage={product.imageUrl}
          twitterCard="summary" // Using a large image card for enhanced visual display
          geoRegion="IN"
        />
        {/* JSON‑LD Structured Data with Publisher & Address Details */}
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(
              {
                "@context": "https://schema.org",
                "@graph": [
                  // Organization details
                  {
                    "@type": "Organization",
                    "@id": "https://coltec-global.com/#organization",
                    name: "COLTEC Global",
                    // Rating details
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: "4.8",
                      reviewCount: "350",
                    },
                    description:
                      "COLTEC Global is a leading manufacturer of Visual Interactive Display Technology, delivering innovative solutions for Education, Corporate, and Retail applications.",
                    url: "https://coltec-global.com",
                    logo: {
                      "@type": "ImageObject",
                      url: "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    },
                    address: [
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                        addressLocality: "Hyderabad",
                        addressRegion: "Telangana",
                        postalCode: "500030",
                        addressCountry: "IN",
                      },
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "COLTEC GLOBAL #41-14-8/2, Opp Mouli Towers, Near Jyothi Convention Hall,Benz circle",
                        addressLocality: "Vijayawada",
                        addressRegion: "Andhra Pradesh",
                        postalCode: "520 010",
                        addressCountry: "IN",
                      },
                    ],
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                    email: "sales@coltec-global.com",
                    contactPoint: [
                      {
                        "@type": "ContactPoint",
                        telephone: "+91 1800 309 8822",
                        contactType: "customer support",
                      },
                    ],
                  },
                  // WebSite details
                  {
                    "@type": "WebSite",
                    "@id": "https://coltec-global.com/#website",
                    url: "https://coltec-global.com",
                    name: "COLTEC Global",
                    publisher: {
                      "@id": "https://coltec-global.com/#organization",
                    },
                    potentialAction: {
                      "@type": "SearchAction",
                      target: `https://coltec-global.com/product-details/${product.slug}?s={search_term_string}`,
                      "query-input": "required name=search_term_string",
                    },
                    inLanguage: "en",
                  },
                  // Updated Product details with additional properties
                  {
                    "@type": "Product",
                    name: `${product.name}`,
                    url: `https://coltec-global.com/product-details/${product.slug}`,
                    description: `${product.description}`,
                    brand: {
                      "@type": "Brand",
                      name: "COLTEC Global Products",
                      url: `https://coltec-global.com/product-details/${product.slug}`,
                      image:
                        "https://content.qeeb.in/coltec/products/banner-product-three.webp",
                    },
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: "4.5",
                      reviewCount: "500",
                    },
                    additionalProperty: [
                      {
                        "@type": "PropertyValue",
                        name: product.name,
                        image: product.imageUrl,
                        value: product.description,
                      },
                      ...product.specs.map((spec, index) => ({
                        "@type": "PropertyValue",
                        name: spec.label,
                        value: spec.value,
                      })),
                    ],
                    // Define offers as a top-level property (an array if there are multiple offers)
                    offers: {
                      "@type": "AggregateOffer",
                      lowPrice: product.price,
                      highPrice: product.originalPrice,
                      priceCurrency: "INR",
                      availability: "https://schema.org/InStock",
                    },
                    // Testimonials embedded as reviews for the product
                    review: [
                      ...testimonials.map((t, i) => ({
                        "@type": "Review",
                        headline: `${product.name}`,
                        itemReviewed: {
                          "@type": "Product",
                          name: t.action,
                        },
                        author: {
                          "@type": "Person",
                          name: t.name,
                        },
                        reviewBody: t.testimonial,
                        reviewRating: {
                          "@type": "Rating",
                          ratingValue: "4.5",
                          bestRating: "5",
                          worstRating: "1",
                        },
                        datePublished: "2024-02-12",
                        publisher: {
                          "@type": "Organization",
                          name: "COLTEC Global",
                        },
                      })),
                    ],
                  },
                  // Breadcrumb for Product Details page
                  {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: "https://coltec-global.com",
                      },
                      {
                        "@type": "ListItem",
                        position: 2,
                        name: "Shop",
                        item: "https://coltec-global.com/shop",
                      },
                      {
                        "@type": "ListItem",
                        position: 3,
                        name: `${product.name}`,
                        item: `https://coltec-global.com/product-details/${product.slug}`,
                      },
                    ],
                  },
                  {
                    "@type": "LocalBusiness",
                    "@id": "https://coltec-global.com/#localbusiness",
                    name: "COLTEC Global",
                    url: "https://coltec-global.com",
                    image:
                      "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    address: {
                      "@type": "PostalAddress",
                      streetAddress:
                        "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                      addressLocality: "Hyderabad",
                      addressRegion: "Telangana",
                      postalCode: "500030",
                      addressCountry: "IN",
                    },
                    telephone: "+91 1800 309 8822",
                    openingHours: "Mon-Sat 09:00-6:00",
                    geo: {
                      "@type": "GeoCoordinates",
                      latitude: "17.3297791",
                      longitude: "78.4381149",
                    },
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                  },
                ],
              },
              null,
              2
            )}
          </script>
        </Helmet>
        <div className="about-us-page pt-14">
          <Header />
          <div className="px-6 lg:px-8 lg:py-14 py-10 max-w-7xl m-auto">
            <div className="grid grid-cols-12 md:gap-5 gap-3">
              {/* Left Column (Thumbnails) */}
              <div className="md:col-span-1 col-span-3 hidden md:block">
                <div className="sticky top-20">
                  <div className="flex flex-col gap-3">
                    {product.gallery.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="cursor-pointer w-20 h-20 object-cover rounded-md"
                        onClick={() => handleThumbnailClick(index)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Center Column (Full Images) */}
              <div className="md:col-span-6 col-span-full">
                <div className="min-h-screen space-y-5 lg:block hidden">
                  {product.gallery.map((image, index) => (
                    <div
                      key={index}
                      ref={(el) => (imagesRef.current[index] = el)}
                    >
                      <img
                        src={image}
                        alt={`Fullimage ${index + 1}`}
                        className="object-contain w-full rounded-lg"
                      />
                    </div>
                  ))}
                </div>
                <div className="lg:hidden block">
                  <Swiper
                    pagination={{ clickable: true }}
                    modules={[Pagination]}
                    className="product-slider-list"
                  >
                    {product.gallery.map((image, index) => (
                      <SwiperSlide>
                        <img
                          src={image}
                          alt={product.name}
                          className="w-full object-contain rounded-lg"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>

              {/* Right Column (Product Info) */}
              <div className="md:col-span-5 col-span-full">
                <div className="sticky top-20">
                  <h1 className="text-2xl font-medium mb-2">{product.name}</h1>

                  <p className="price text-[21px] font-semibold text-black">
                    ₹{product.price}
                    <span className="line-through ml-2 font-normal text-[#797979]">
                      ₹{product.originalPrice}
                    </span>
                  </p>

                  {/* Description */}
                  <div className="product-description text-[13px] text-[#797979] mt-4">
                    <p>{product.description}</p>
                  </div>
                  {/* Panel Size Dropdown */}
                  <div className="my-4">
                    <label
                      htmlFor="panelSize"
                      className="block mb-2 font-normal text-sm"
                    >
                      Panel size required
                    </label>
                    <select
                      id="panelSize"
                      className="border w-full border-gray-300 rounded p-2"
                      onChange={handleSizeChange}
                      value={currentSize}
                    >
                      {dropdownSizes.map((sz) => (
                        <option key={sz} value={sz}>
                          {sz}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Quantity Selector */}
                  <div className="my-4 flex items-end md:items-start justify-between">
                    <label className="block mb-2 font-normal text-sm">
                      Quantity required
                    </label>
                    <div className="flex items-center space-x-3 border rounded">
                      <button
                        type="button"
                        onClick={decreaseQuantity}
                        className="px-3 py-1 bg-white border-r rounded-l text-[#4282ff]"
                      >
                        –
                      </button>
                      <span>{quantity}</span>
                      <button
                        type="button"
                        onClick={increaseQuantity}
                        className="px-3 py-1 bg-white border-l rounded-r text-[#4282ff]"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Conditionally Render the Button */}
                  <div className="flex justify-between md:justify-start space-x-4">
                    {!addedToCart ? (
                      <button
                        onClick={handleAddToCart}
                        className="bg-[#4282ff] w-full text-white py-2.5 font-semibold text-[13px] md:text-base hover:bg-[#4282ff]/90 px-4 rounded-md md:rounded-xl"
                      >
                        Add to Cart
                      </button>
                    ) : (
                      <button
                        onClick={handleViewCart}
                        className="bg-black w-full text-white py-2.5 font-semibold text-[13px] md:text-base hover:bg-gray-800 px-4 rounded-md md:rounded-xl"
                      >
                        View Cart
                      </button>
                    )}
                    <Link
                      to="/shop"
                      className="hover:bg-orange-500 w-full text-center text-white py-2.5 text-[13px] md:text-base font-semibold bg-orange-400 px-4 rounded-md md:rounded-xl"
                    >
                      Continue Shopping
                    </Link>
                  </div>

                  {product.content && (
                    <>
                      <div
                        className="prose"
                        dangerouslySetInnerHTML={{ __html: product.content }}
                      ></div>
                    </>
                  )}

                  {/* Specs */}
                  {product.specs && product.specs.length > 0 && (
                    <div className="product-specifications mt-6">
                      <h2 className="text-xl font-semibold mb-4">
                        Technical Details
                      </h2>
                      <div className="border-t">
                        {product.specs.map((spec, index) => (
                          <div
                            key={index}
                            className="flex justify-between md:px-5 items-center mb-2 border-b py-3"
                          >
                            <span className="font-medium text-[12px] w-1/2">
                              {spec.label}:
                            </span>
                            <span className="text-[12px] w-1/2 text-left">
                              {spec.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="px-6 lg:px-8 lg:pb-14 pb-10 max-w-7xl m-auto">
            {/* Pass the product's ID to CustomerReviews */}
            <CustomerReviews productId={product.id} />
          </div>
          {/* Conditionally Render Sections Based on Product Type */}
          {isInteractive ? (
            <>
              <div className="relative px-5 lg:px-8 lg:pb-14 pb-10 lg:pt-5 max-w-7xl mx-auto">
                <div>
                  {/* The "slides container" stacked vertically */}
                  <h2 className="w-full md:text-center mb-3 max-w-2xl mx-auto md:leading-9">
                    <span className="text-black md:text-[30px] text-[22px] font-semibold">
                      <span className="text-[#79adf3]">
                        Revolutionize Learning{" "}
                      </span>
                      with Smart Interactive Flat Panels
                    </span>
                  </h2>
                  <p className="md:text-center text-sm md:text-base mx-auto max-w-3xl text-black mb-12">
                    Unlock the future of education with cutting-edge technology
                    that drives engagement, enhances focus, and fosters
                    collaboration—making every lesson impactful and
                    unforgettable.
                  </p>
                  {/* Render each slide as a full-screen panel */}
                  <div className="grid md:grid-cols-2 lg:gap-20 gap-5 items-start">
                    {/* Left column: Active step's image */}
                    <div className="flex justify-center">
                      <img
                        src={activeStep.image}
                        alt={activeStep.alt}
                        className="object-cover w-full h-auto rounded shadow"
                      />
                    </div>

                    {/* Right column: vertical timeline & step content */}
                    <div className="flex items-start justify-center">
                      {/* Timeline (line + circles) */}
                      <div className="relative flex flex-col items-center mr-6">
                        {/* Step circles: highlight only the active step */}
                        {steps.map((step, idx) => (
                          <div key={idx} className="flex flex-col items-center">
                            <div
                              className={`flex items-center justify-center ${
                                idx === stepIndex
                                  ? "w-14 h-14 bg-[#3687fe] rounded-lg shadow-[0px_4px_16px_0px_rgba(203,211,222,0.60)] border"
                                  : "w-14 h-14 bg-gradient-to-r from-[#fafbff] to-[#edf2ff] rounded-lg shadow-[0px_4px_16px_0px_rgba(203,211,222,0.60)] border border-[#d8e8ec]"
                              }`}
                            >
                              <img
                                src={step.icon}
                                alt={step.alt}
                                className={
                                  idx === stepIndex ? "invert brightness-0" : ""
                                }
                              />
                            </div>
                            {/* Only render a connecting line if this is not the last step */}
                            {idx < steps.length - 1 && (
                              <div className="relative h-[50px] my-4 w-[2px] bg-gray-300">
                                <div
                                  className="absolute top-0 left-0 w-[2px] bg-[#3687fe] transition-all duration-300"
                                  style={{
                                    height: `${computeFillHeightForLine(
                                      idx
                                    )}px`,
                                  }}
                                ></div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Step text */}
                      <div className="flex flex-col md:space-y-[53px] space-y-[40px]">
                        {steps.map((step, idx) => (
                          <div key={idx}>
                            <h3
                              className={`md:text-xl font-semibold mb-2 ${
                                idx === stepIndex
                                  ? "text-[#3687fe]"
                                  : "text-black"
                              }`}
                            >
                              {step.title}
                            </h3>
                            <p className="text-[#494949] text-xs font-normal lg:w-[350px]">
                              {step.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-[#fbfcff] to-[#edf1fb]">
                {/* <!-- Details content here --> */}
                <div className="px-5 lg:px-8 lg:py-14 pb-10 max-w-7xl mx-auto">
                  <h2 className="md:text-center max-w-2xl mx-auto md:text-3xl text-[24px] font-bold text-black">
                    <span className="text-[#79adf3]">
                      Revolutionize Learning
                    </span>{" "}
                    with Smart Interactive Boards
                  </h2>
                  <p className="md:text-center mx-auto max-w-3xl text-black md:mt-4">
                    Unlock the future of education with cutting-edge technology
                    that drives engagement, enhances focus, and fosters
                    collaboration—making every lesson impactful and
                    unforgettable.
                  </p>

                  {/* Feature Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-0 mt-12 relative">
                    {features.map((feature, index) => (
                      <div
                        key={feature.id}
                        className={`relative md:p-8 px-4 py-6 ${feature.border}`}
                      >
                        {feature.dot && (
                          <div
                            className={`absolute right-[-5px] bottom-[-5px] z-[1] ${feature.dot_block}`}
                          >
                            <img alt="Feature-dot" src={feature.dot} />
                          </div>
                        )}
                        <div className="text-4xl">
                          <img
                            alt={feature.title}
                            src={feature.icon}
                            className="md:h-[50px] h-[30px]"
                          />
                        </div>
                        <h3 className="mt-4 md:text-lg text-sm md:leading-6 font-semibold">
                          {feature.title}
                        </h3>
                        <p className="mt-2 text-xs text-gray-500">
                          {feature.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* <!-- FAQs content here --> */}
              <div className="px-5 lg:px-8 lg:py-14  py-10 max-w-7xl m-auto landing-faq">
                <div className="max-w-2xl mx-auto md:text-center mb-8">
                  <span className="text-[#79adf3] text-2xl md:text-[32px] font-semibold font-['Poppins'] md:leading-[48px]">
                    Frequently
                  </span>
                  <span className="text-black md:text-[32px] text-2xl font-semibold font-['Poppins'] md:leading-[48px]">
                    {" "}
                    Asked Questions (FAQs)
                  </span>
                </div>
                <FAQ data={faqData} />
              </div>
              <div className="bg-[#f5f5f5] lg:pb-12 pb-14 pt-10 lg:pt-0">
                <TestimonialCarousel />
              </div>
              <div className="py-10">
                <div className="lg:text-center px-5 md:px-0">
                  <h3 className="md:text-[32px] text-2xl font-semibold leading-8 mb-8">
                    We are Proud to{" "}
                    <span className="text-[#79adf3] md:text-[32px] text-2xl font-semibold">
                      Partner
                    </span>{" "}
                    with
                  </h3>
                </div>
                <div className="max-w-7xl mx-auto">
                  <div className="marquee">
                    {clientImages.map((client, index) => (
                      <img
                        key={index}
                        src={client.src}
                        alt={client.alt}
                        loading="lazy" // Enable lazy loading
                        className="md:h-20 h-16"
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="w-full relative h-[156px] bg-gradient-to-l from-[#6d91ff] to-[#2eaccf]">
                <div className="px-6 py-8 sm:px-6 lg:py-14 lg:px-8 max-w-7xl mx-auto">
                  <div className="flex md:flex-row flex-col justify-start items-start md:justify-between md:items-center">
                    <h2 className="lg:text-[28px] text-[19px] font-medium leading-[1.3] text-white md:max-w-3xl">
                      Enhance learning with COLTEC Interactive Flat Panels!
                    </h2>
                    <div className="flex items-center justify-center gap-x-6 mt-4 md:mt-0">
                      <button
                        onClick={openModal}
                        className="flex items-center rounded-md bg-white px-5 py-2 text-xs md:text-sm font-medium text-[#0A78B9] shadow-sm hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                      >
                        Book a Free Demo Today!
                        <img
                          alt="right-arrow"
                          src="https://content.qeeb.in/coltec/blue-arrow.svg"
                          className="h-2 pl-2"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : isActiveLED ? (
            <>
              {/* Active LED Video Wall Specific Content */}
              <div className="active-led-block px-5 lg:px-0 max-w-5xl mx-auto">
                <InertiaFeatures />
              </div>
              {/* <!-- FAQs content here --> */}
              <div className="px-5 lg:px-8 lg:py-14  py-10 max-w-7xl m-auto landing-faq">
                <div className="max-w-2xl mx-auto md:text-center mb-8">
                  <span className="text-[#79adf3] text-2xl md:text-[32px] font-semibold font-['Poppins'] md:leading-[48px]">
                    Frequently
                  </span>
                  <span className="text-black md:text-[32px] text-2xl font-semibold font-['Poppins'] md:leading-[48px]">
                    {" "}
                    Asked Questions (FAQs)
                  </span>
                </div>
                <FAQ data={faqData} />
              </div>
            </>
          ) : isCommercial ? (
            <>
              {/* Commercial Display Specific Content */}
              <div className="commercial-display-block px-5 lg:px-0 max-w-5xl mx-auto">
                <VistaFeatures />
                {/* Additional Commercial Display-specific content can be added here */}
              </div>

              {/* <!-- FAQs content here --> */}
              <div className="px-5 lg:px-8 lg:py-14 py-10 max-w-7xl m-auto landing-faq">
                <div className="max-w-2xl mx-auto md:text-center mb-8">
                  <span className="text-[#79adf3] text-2xl md:text-[32px] font-semibold font-['Poppins'] md:leading-[48px]">
                    Frequently
                  </span>
                  <span className="text-black md:text-[32px] text-2xl font-semibold font-['Poppins'] md:leading-[48px]">
                    {" "}
                    Asked Questions (FAQs)
                  </span>
                </div>
                <FAQ data={faqData} />
              </div>
            </>
          ) : null}
          <Footer />
          <ScrollToTopButton />
          <WhatsAppMsg />
          {/* Modal popup */}
          {isModalOpen && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4 py-6">
                <div
                  className="fixed inset-0 transition-opacity"
                  onClick={closeModal}
                >
                  <div className="absolute inset-0 bg-black opacity-50"></div>
                </div>
                <div className="bg-[#f1f1f1] rounded-lg overflow-hidden shadow-xl transform transition-all max-w-[770px] w-full z-10">
                  <div className="flex items-center justify-between p-4 bg-white">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Contact Form
                    </h3>
                    <div>
                      <button onClick={closeModal}>
                        <img
                          alt="close-icon"
                          className="w-6"
                          src="https://content.qeeb.in/coltec/close-icon.svg"
                        />
                      </button>
                    </div>
                  </div>
                  <div className="modal-iframe">
                    <iframe
                      src="/modal-form.html"
                      title="outer-iframe"
                      className="w-full h-full search-iframe"
                      style={{ border: "none" }}
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Toast Notifications */}
      {toast.showToast && (
        <div
          className={`fixed top-4 right-4 p-4 z-50 rounded shadow ${
            toast.type === "success"
              ? "bg-green-50 text-green-600"
              : "bg-red-50 text-red-600"
          }`}
        >
          <div className="flex">{toast.message}</div>
        </div>
      )}
    </>
  );
};

export default ProductDetails;
